import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnDestroy,
  Output,
  runInInjectionContext
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import flatpickr from 'flatpickr';
import { German } from 'flatpickr/dist/l10n/de.js';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { Options as FlatpickrOptions } from 'flatpickr/dist/types/options';
import { Observable } from 'rxjs';

@Directive({
  selector: '[wctDatepicker]',
  standalone: true,
  exportAs: 'wctDatepicker',
})
export class DatepickerDirective implements AfterViewInit, OnDestroy {
  public static readonly DATA_FORMAT = 'Y-m-d';
  public static readonly DISPLAY_FORMAT = 'd.m.Y';

  private readonly inputElement: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly injector: Injector = inject(Injector);

  @Input({ required: false }) public recreateInstance: Observable<unknown> | undefined;

  @Input({ required: false }) public set date(date: string) {
    this._date = date;
    this.flatpickrInstance?.setDate(date, false);
  }

  @Input({ required: false }) public set minDate(value: string | number | Date | undefined) {
    if (value === undefined) {
      delete this.flatpickrOptions.minDate;
    }

    this.flatpickrOptions.minDate = value;
  }

  @Input({ required: false }) public set maxDate(value: string | number | Date | undefined) {
    if (value === undefined) {
      delete this.flatpickrOptions.maxDate;
    }

    this.flatpickrOptions.maxDate = value;
  }

  @Input({ required: false }) public set disabledDates(value: null | ((date: Date) => boolean)) {
    this.flatpickrOptions.disable = value ? [value] : [];
  }

  @Input({ required: false }) public set enabledDates(value: null | ((date: Date) => boolean) | string[]) {
    if (!value) {
      delete this.flatpickrOptions.enable;
      return;
    }

    if (Array.isArray(value)) {
      this.flatpickrOptions.enable = [...value];
      return;
    }

    this.flatpickrOptions.enable = [value];
  }

  @Input({ required: true }) public appendTo!: HTMLElement;

  @Output() public readonly dateChange: EventEmitter<string> = new EventEmitter();

  private readonly flatpickrOptions: FlatpickrOptions;
  private flatpickrInstance?: FlatpickrInstance;

  private _date?: string;

  constructor() {
    this.flatpickrOptions = {
      dateFormat: DatepickerDirective.DATA_FORMAT,
      locale: German,

      position: 'below center',

      onChange: this.setDate,
      mode: 'single',
    };
  }

  ngOnDestroy(): void {
    this.dateChange.complete();
    this.flatpickrInstance?.destroy();
  }

  ngAfterViewInit(): void {
    this.flatpickrOptions.appendTo = this.appendTo;

    if (this.recreateInstance) {
      this.bindDelayedInstanceLifetime();
    } else {
      this.createInstance();
    }
  }

  private bindDelayedInstanceLifetime(): void {
    /** Some flatpickr options don't update unless the instance is recreated. */
    runInInjectionContext(this.injector, () =>
      this.recreateInstance!.pipe(takeUntilDestroyed()).subscribe(() => {
        setTimeout(() => {
          this.createInstance();
        });
      })
    );
  }

  private createInstance(): void {
    this.flatpickrInstance = flatpickr(this.inputElement.nativeElement, this.flatpickrOptions);

    if (this._date) {
      this.flatpickrInstance.setDate(this._date, false);
    }
  }

  private readonly setDate = (_: Date | Array<Date>, dateStr: string): void => {
    this._date = dateStr;
    this.dateChange.next(dateStr);
  };
}
